/**
 * @file components/chat/AgentDebugPanel.tsx
 * CHAT-06b — Timeline des étapes de traitement agent (ADMIN/ROOT uniquement).
 *
 * Affiché sous la bulle agent si `statusDebugLog` est non vide.
 * Contrôlé par VITE_SHOW_AGENT_DEBUG (false en production publique).
 * Visible uniquement si le backend a envoyé des champs `_debug`
 * (ce qui n'arrive que pour les rôles ADMIN/ROOT).
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FlaskConical } from 'lucide-react';
import { StatusDebugLog } from './types';

/** Libellés français des étapes */
const STEP_LABELS: Record<string, string> = {
  intent:     'Intent',
  memory:     'Mémoire',
  rag:        'RAG',
  tool:       'Outil',
  generating: 'Génération',
};

interface AgentDebugPanelProps {
  log: StatusDebugLog;
  className?: string;
}

/**
 * Timeline compacte des étapes de traitement avec données techniques.
 * Visible uniquement quand VITE_SHOW_AGENT_DEBUG=true et que le backend
 * a envoyé des champs `_debug` (rôles ADMIN/ROOT).
 */
export default function AgentDebugPanel({ log, className = '' }: AgentDebugPanelProps): React.ReactElement | null {
  const [expanded, setExpanded] = useState(false);

  // Le panneau n'est visible que si le backend a envoyé des _debug (ADMIN/ROOT uniquement).
  // Pas de gate VITE_SHOW_AGENT_DEBUG : le filtre est côté backend — si log est non vide,
  // c'est que l'utilisateur est admin et que la donnée est autorisée.
  if (log.length === 0) return null;

  return (
    <div className={`ml-9 mt-1 w-fit max-w-[78%] ${className}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={expanded}
        aria-label={expanded ? 'Masquer le journal de traitement' : 'Afficher le journal de traitement'}
      >
        <FlaskConical size={11} aria-hidden />
        <span>Debug</span>
        {expanded ? <ChevronDown size={11} aria-hidden /> : <ChevronRight size={11} aria-hidden />}
      </button>

      {expanded && (
        <ol className="mt-1 space-y-1 rounded border border-border bg-muted/40 px-3 py-2">
          {log.map((entry, idx) => (
            <li key={idx} className="text-[10px]">
              <span className="font-semibold text-foreground">
                {STEP_LABELS[entry.step] ?? entry.step}
              </span>
              {entry.debug && Object.keys(entry.debug).length > 0 && (
                <ul className="ml-2 mt-0.5 space-y-0.5 text-muted-foreground">
                  {Object.entries(entry.debug).map(([k, v]) => (
                    <li key={k}>
                      <span className="font-mono">{k}</span>
                      {' = '}
                      <span className="font-mono text-foreground">
                        {Array.isArray(v) ? JSON.stringify(v) : String(v)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
